import React, { useState } from 'react';
import Dialog from './Dialog';
import useDialogMutation from '../hooks/useDialogMutation';

function withScheme(value) {
  const text = value.trim();
  if (!text) return text;
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`;
}

export function validateUrl(value) {
  const text = value.trim();
  if (!text) return 'Website URL is required.';
  let parsed;
  try {
    parsed = new URL(text);
  } catch {
    return 'Enter a full URL starting with http:// or https://';
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return 'Only http:// and https:// links are allowed.';
  }
  if (!parsed.hostname) return 'Enter a valid website address.';
  if (!parsed.hostname.includes('.') && parsed.hostname !== 'localhost') {
    return 'Enter a valid website address.';
  }
  return '';
}

export function normalizeWebsiteUrl(value) {
  return withScheme(value);
}

function AppDialog({ title, submitLabel, app, sections, sectionId, onSubmit, onClose }) {
  const [name, setName] = useState(app?.name || '');
  const [url, setUrl] = useState(app?.url || '');
  const [errors, setErrors] = useState({});
  const { run, saving, error: saveError } = useDialogMutation();
  const sectionTitle = sections.find((section) => section.id === sectionId)?.title;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const next = {};
    if (!name.trim()) next.name = 'Application name is required.';
    const normalizedUrl = withScheme(url);
    const urlError = validateUrl(normalizedUrl);
    if (urlError) next.url = urlError;
    setErrors(next);
    if (Object.keys(next).length || saving) return;
    await run(() => onSubmit({ name: name.trim(), url: normalizedUrl }, sectionId), onClose);
  };

  return (
    <Dialog title={title} onClose={onClose} locked={saving}>
      <form onSubmit={handleSubmit} noValidate>
        {sectionTitle && (
          <p className="dialog-meta">
            Section: <span>{sectionTitle}</span>
          </p>
        )}

        <label className="dialog-label" htmlFor="app-name">Application Name</label>
        <input
          id="app-name"
          className="dialog-input"
          value={name}
          maxLength={40}
          autoFocus
          disabled={saving}
          aria-invalid={Boolean(errors.name)}
          onChange={(event) => setName(event.target.value)}
        />
        {errors.name && <p className="dialog-error">{errors.name}</p>}

        <label className="dialog-label field-gap" htmlFor="app-url">Website URL</label>
        <input
          id="app-url"
          className="dialog-input"
          type="url"
          value={url}
          placeholder="https://example.com/page"
          disabled={saving}
          aria-invalid={Boolean(errors.url)}
          onChange={(event) => setUrl(event.target.value)}
        />
        {errors.url && <p className="dialog-error">{errors.url}</p>}
        {saveError && <p className="dialog-error" role="alert">{saveError}</p>}

        <div className="dialog-actions">
          <button type="button" className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : submitLabel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export default AppDialog;